import dayjs from 'dayjs'
import { LocalStorageHelper } from './localStorageHelper'
import { UserDataType } from '../types/UserDataType'

export const READING_TIME_STORAGE_KEYS = {
  DAILY_READING_TIME: 'daily_reading_time',
  TOTAL_READING_TIME: 'total_reading_time',
}

export const getTodayKey = (): string => {
  return dayjs().format('YYYY-MM-DD')
}

export const incrementReadingTimeSummary = (
  seconds: number,
  currentReadingTime?: UserDataType['readingTime'],
) => {
  const today = getTodayKey()

  let {
    today: cloudToday = 0,
    thisWeek = 0,
    total = 0,
    lastUpdated = today,
  } = currentReadingTime || {}

  // Handle rollover if dates don't match
  if (lastUpdated !== today) {
    cloudToday = 0
    if (dayjs(lastUpdated).isBefore(dayjs().startOf('isoWeek'))) {
      thisWeek = 0
    }
  }

  return {
    today: cloudToday + seconds,
    thisWeek: thisWeek + seconds,
    total: total + seconds,
    lastUpdated: today,
  }
}

export const saveReadingTimeToLocalStorage = (seconds: number) => {
  if (seconds <= 0) return

  const todayKey = getTodayKey()
  const existingDailyData =
    LocalStorageHelper.get<{ [date: string]: number }>(
      READING_TIME_STORAGE_KEYS.DAILY_READING_TIME,
    ) || {}

  existingDailyData[todayKey] = (existingDailyData[todayKey] || 0) + seconds
  LocalStorageHelper.set(READING_TIME_STORAGE_KEYS.DAILY_READING_TIME, existingDailyData)

  // Update all-time total
  const currentTotal =
    LocalStorageHelper.get<number>(READING_TIME_STORAGE_KEYS.TOTAL_READING_TIME) || 0
  LocalStorageHelper.set(
    READING_TIME_STORAGE_KEYS.TOTAL_READING_TIME,
    currentTotal + seconds,
  )
}

export const mergeAndMigrateReadingTime = (
  cloudReadingTime?: UserDataType['readingTime'],
): UserDataType['readingTime'] => {
  const dailyReadingData =
    LocalStorageHelper.get<Record<string, number>>(
      READING_TIME_STORAGE_KEYS.DAILY_READING_TIME,
    ) || {}
  const totalReadingData =
    LocalStorageHelper.get<number>(READING_TIME_STORAGE_KEYS.TOTAL_READING_TIME) || 0

  const today = getTodayKey()
  const startOfISOWeek = dayjs().startOf('isoWeek').format('YYYY-MM-DD')

  // Calculate the true local total by summing the history if needed
  const calculatedLocalTotal = Object.values(dailyReadingData).reduce(
    (sum: number, val: unknown) => sum + (typeof val === 'number' ? val : 0),
    0,
  )
  const actualLocalTotal = Math.max(Number(totalReadingData) || 0, calculatedLocalTotal)

  if (!cloudReadingTime || cloudReadingTime.total < actualLocalTotal) {
    // Migrate from localStorage if cloud is empty OR if local has MORE data than cloud
    const todaySeconds = (dailyReadingData as any)[today] || 0
    const thisWeekSeconds = Object.entries(dailyReadingData as any)
      .filter(([date]) => date >= startOfISOWeek)
      .reduce((sum, [_, val]) => sum + (val as number), 0)

    return {
      today: Math.max(todaySeconds, cloudReadingTime?.today || 0),
      thisWeek: Math.max(thisWeekSeconds, cloudReadingTime?.thisWeek || 0),
      total: actualLocalTotal,
      lastUpdated: today,
    }
  } else {
    // Handle rollovers for existing cloud data
    const lastUpdated = cloudReadingTime.lastUpdated
    let { today: cloudToday, thisWeek: cloudThisWeek } = cloudReadingTime

    if (lastUpdated !== today) {
      cloudToday = 0
      if (dayjs(lastUpdated).isBefore(dayjs().startOf('isoWeek'))) {
        cloudThisWeek = 0
      }
    }

    return {
      today: cloudToday,
      thisWeek: cloudThisWeek,
      total: cloudReadingTime.total,
      lastUpdated: today,
    }
  }
}
